import * as React from 'react'
import { cn } from '@/lib/utils'
import { Label } from '@/components/ui/label'
import { TextboxProps } from '@/formBuilder/node.types'
import { useState } from 'react'
import { UserAvatar } from '@/assets/icons/UserAvatar'
import { EmailIcon } from '@/assets/icons/EmailIcon'

export interface InputProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  node: TextboxProps
  handleChange: (key: string, value: string) => void
  button?: React.ReactNode
  userAvatar?: boolean
  addButton?: boolean
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      node,
      handleChange,
      onKeyDown,
      className,
      button,
      userAvatar,
      addButton,
      ...props
    },
    ref,
  ) => {
    const [inputValue, setInputValue] = useState(node?.value)

    const handleInputChange = (field: string, value: string) => {
      handleChange(field, value)
      setInputValue(value)
    }

    React.useEffect(() => {
      setInputValue(node?.value)
    }, [node])

    return (
      <div
        className={cn('w-full', node?.childClass && node.childClass)}
        style={node?.hidden ? { display: 'none' } : { display: 'block' }}
      >
        <Label
          htmlFor={node?.id}
          className='mb-1.5 block text-[14px] font-medium text-[#344054]'
        >
          {node?.label}
        </Label>

        <div className='relative w-full'>
          {/* Left Icon */}
          {userAvatar && (
            <div className='absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none'>
              <UserAvatar className='w-4 h-4 text-[#667085]' />
            </div>
          )}
          {addButton && node.id === 'email' && (
            <div className='absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none'>
              <EmailIcon className='w-4 h-4 text-[#667085]' />
            </div>
          )}

          {/* Input */}
          <input
            key={node?.id}
            id={node?.id}
            name={node?.fieldName}
            placeholder={node?.placeholder}
            value={inputValue || node.value}
            hidden={false}
            disabled={node?.disabled}
            onKeyDown={onKeyDown}
            readOnly={node?.readOnly}
            onChange={e => handleInputChange(node?.fieldName, e.target.value)}
            ref={ref}
            {...props}
            className={cn(
              `
    h-[40px]
    w-full
    rounded-[6px]
    border
    border-[#D0D5DD]
    text-[14px]
    text-[#101828]
    placeholder:text-[#667085]
    outline-none
    transition-all
    focus:border-[#98A2B3]
    focus:ring-1
    focus:ring-[#98A2B3]
    disabled:cursor-not-allowed
    disabled:opacity-50
  `,

              userAvatar || (addButton && node.id === 'email')
                ? 'bg-[#F9FAFB] pl-9 pr-10'
                : 'bg-[#FFFFFF] px-3',

              addButton && 'border-primary-foreground pr-10',

              className,
            )}
          />

          {/* Right Button/Icon */}
          {button && (
            <div className='absolute right-3 top-1/2 -translate-y-1/2 flex items-center'>
              {button}
            </div>
          )}
        </div>
      </div>
    )
  },
)
Input.displayName = 'Input'

export { Input }
